import { FC, useEffect, useState } from "react";
import { Button, Modal, Select, Space, Spin, Tabs, message } from "antd";
import { Checkbox } from "../../../design-system/components/Checkbox";
import { Tooltip } from "../../../design-system/components/Tooltip";

import {
    AnalysisOutput,
    AnalysisOutputType,
    DealAnalysisOutput,
    FlexibleAnalysisOutput,
    Project,
    ProjectAnalysis,
    ProjectAnalysisStatus,
    ProjectType,
    RfxAnalysisOutput,
    StructuredOutput,
    StructuredOutputType,
    isDealOutput,
    isFlexibleOutput,
    isRfxOutput,
} from "../../../types/ProjectTypes";
import { getAnalysisOutput, saveAnalysisOutput } from "../../../common/api/ProjectClient";
import { handleError } from "../../../common/ErrorHandling";
import { DealInfoTab } from "./ModalSaveAnalysisOutput/DealInfoTab";
import { SummaryTab } from "./ModalSaveAnalysisOutput/SummaryTab";
import { DealReportTab } from "./ModalSaveAnalysisOutput/DealReportTab";
import { RfxReportTab } from "./ModalSaveAnalysisOutput/RfxReportTab";
import { RfxVendorsTab } from "./ModalSaveAnalysisOutput/RfxVendorsTab";
import { LeversTab } from "./ModalSaveAnalysisOutput/LeversTab";
import { FlexibleContentTab } from "./ModalSaveAnalysisOutput/FlexibleContentTab";
import { AttachmentsTab } from "./ModalSaveAnalysisOutput/AttachmentsTab";
import {
    FlexibleStructuredOutputsTab,
    createDealStructuredOutput,
    createRfxStructuredOutput,
} from "./ModalSaveAnalysisOutput/FlexibleStructuredOutputsTab";

import { ProjectViewState } from "../ProjectViewState";

interface ModalSaveAnalysisOutputProps {
    open: boolean;
    state: ProjectViewState;
    onClose: () => void;
    onSuccess: () => void | Promise<void>;
}

function initDealOutput(
    project: Project,
    existing: DealAnalysisOutput | undefined
): DealAnalysisOutput {
    const initial: DealAnalysisOutput = {
        type: AnalysisOutputType.DEAL,
        summary: existing?.summary,
        report: existing?.report,
        levers: existing?.levers && existing.levers.length > 0 ? existing.levers : undefined,
        sourceUrls: existing?.sourceUrls,
    };

    // Deal info: prefer saved output, fall back to orderForm for v2+
    if (existing?.dealInfo) {
        initial.dealInfo = existing.dealInfo;
    } else if (project.orderForm) {
        initial.dealInfo = {
            vendorKey: project.orderForm.vendorKey,
            value: Math.round(project.orderForm.value * 100),
            orderStartDate: project.orderForm.orderStartDate?.toString(),
            orderEndDate: project.orderForm.orderEndDate?.toString(),
            orderScore: project.orderForm.orderScore,
            optimizedScore: project.orderForm.optimizedScore,
            autoRenewal: project.orderForm.autoRenewal,
            status: project.orderForm.negotiationStatus,
            ownerId: project.orderForm.ownerId,
            increase:
                project.orderForm.increase != null
                    ? Math.round(project.orderForm.increase * 100)
                    : undefined,
            targetedSavings:
                project.orderForm.targetedSavings != null
                    ? Number(project.orderForm.targetedSavings)
                    : undefined,
            realizedSavings:
                project.orderForm.realizedSavings != null
                    ? Number(project.orderForm.realizedSavings)
                    : undefined,
        };
    }

    // Pre-populate owner from project owner when not already set
    if (!initial.dealInfo?.ownerId && project.ownerId) {
        initial.dealInfo = {
            ...initial.dealInfo,
            ownerId: project.ownerId,
        } as NonNullable<DealAnalysisOutput["dealInfo"]>;
    }

    return initial;
}

function initRfxOutput(
    project: Project,
    existing: RfxAnalysisOutput | undefined
): RfxAnalysisOutput {
    // Candidate vendors: prefer saved output, fall back to the project's RFx for v2+
    return {
        type: AnalysisOutputType.RFX,
        report: existing?.report,
        sourceUrls: existing?.sourceUrls,
        vendors:
            existing?.vendors ??
            project.rfx?.vendors.map(({ vendorKey }) => ({ vendorKey })),
    };
}

function initFlexibleOutput(
    project: Project,
    existing: FlexibleAnalysisOutput | undefined
): FlexibleAnalysisOutput {
    return {
        type: AnalysisOutputType.FLEXIBLE,
        content: existing?.content || "",
        // On deal projects with a linked order form, and RFx projects with an
        // RFx, pre-create the structured output prefilled from it instead of
        // making the user add the only sensible option by hand. Without one
        // there is nothing to prefill, so leave the list empty and let the user
        // add one if needed.
        structuredOutputs: existing?.structuredOutputs ?? seedStructuredOutputs(project),
    };
}

function seedStructuredOutputs(project: Project): StructuredOutput[] | undefined {
    if (project.type === ProjectType.DEAL && project.orderForm) {
        return [createDealStructuredOutput(project)];
    }
    if (project.type === ProjectType.RFX && project.rfx) {
        return [createRfxStructuredOutput(project)];
    }
    return undefined;
}

function defaultOutputType(analysis: ProjectAnalysis): AnalysisOutputType {
    return analysis.outputType ?? AnalysisOutputType.FLEXIBLE;
}

function initOutputForType(
    outputType: AnalysisOutputType,
    project: Project,
    existingOutput?: AnalysisOutput
): AnalysisOutput {
    switch (outputType) {
        case AnalysisOutputType.DEAL:
            return initDealOutput(
                project,
                isDealOutput(existingOutput) ? existingOutput : undefined
            );
        case AnalysisOutputType.RFX:
            return initRfxOutput(project, isRfxOutput(existingOutput) ? existingOutput : undefined);
        case AnalysisOutputType.FLEXIBLE:
            return initFlexibleOutput(
                project,
                isFlexibleOutput(existingOutput) ? existingOutput : undefined
            );
    }
}

// Deal and RFX are legacy output types: they are only offered when the analysis
// already has such an output. New outputs are always Flexible.
const outputTypeOptions = [
    { label: "Deal (legacy)", value: AnalysisOutputType.DEAL },
    { label: "RFX (legacy)", value: AnalysisOutputType.RFX },
    { label: "Flexible", value: AnalysisOutputType.FLEXIBLE },
];

/** Whether `output` carries a report, as the server reads it (ILProjectAnalysis.outputHasReport). */
function outputHasReport(output: AnalysisOutput): boolean {
    if (isFlexibleOutput(output)) return output.content.trim().length > 0;
    return !!output.report;
}

/**
 * Whether saving `output` delivers the analysis, as the server decides it:
 * the save completes the analysis, or lands the first report on one that
 * completed at submit. A delivery always notifies the project's users.
 */
function saveDelivers(analysis: ProjectAnalysis, output: AnalysisOutput): boolean {
    return (
        analysis.status !== ProjectAnalysisStatus.COMPLETED ||
        (!analysis.hasReport && outputHasReport(output))
    );
}

function useAnalysisOutputState({
    open,
    state,
    onClose,
    onSuccess,
}: ModalSaveAnalysisOutputProps) {
    const { project, analysis } = state;
    const [output, setOutput] = useState<AnalysisOutput>(
        initOutputForType(defaultOutputType(analysis), project)
    );
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const resetState = () => {
        setOutput(initOutputForType(defaultOutputType(analysis), project));
        setSaving(false);
    };

    // Re-initialize output state only when the modal opens (not on every project re-fetch,
    // which would discard in-progress edits — e.g. after uploading an attachment).
    // Fetches the existing analysis output from the server. The analysis summary
    // says whether there is one (outputType is set only when an output was
    // saved), so a fresh analysis just gets the defaults without a round trip.
    useEffect(() => {
        if (!open) return;
        // Reset to defaults synchronously so the form has a sensible shape while
        // the fetch is in flight; the fetched data overwrites it on success.
        setOutput(initOutputForType(defaultOutputType(analysis), project));
        if (!analysis.outputType) return;
        let cancelled = false;
        setLoading(true);
        getAnalysisOutput(project.id, analysis.version).then(
            (existing) => {
                if (cancelled) return;
                const outputType = existing?.type ?? defaultOutputType(analysis);
                setOutput(initOutputForType(outputType, project, existing));
                setLoading(false);
            },
            () => {
                // The output could not be loaded: defaults already applied above.
                if (cancelled) return;
                setLoading(false);
            }
        );
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, analysis.version]);

    const handleOutputTypeChange = (newType: AnalysisOutputType) => {
        if (newType === output.type) return;
        setOutput(initOutputForType(newType, project));
    };

    // Older outputs carried savings the old way: a legacy deal output as the
    // summary's [low, high] range and realized amount, a flexible one with a
    // low/high pair in dealInfo. Save the shape the dialog edits today: the
    // target and realized savings on dealInfo, nothing else. The summary keeps
    // its own values; the Summary tab renders them.
    const withCurrentSavingsShape = (o: AnalysisOutput): AnalysisOutput => {
        if (isDealOutput(o) && o.dealInfo) {
            const [low, high] = o.summary?.estimatedSavings ?? [];
            return {
                ...o,
                dealInfo: {
                    ...o.dealInfo,
                    targetedSavings:
                        o.dealInfo.targetedSavings ??
                        (low != null && high != null ? Math.round((low + high) / 2) : undefined),
                    realizedSavings: o.dealInfo.realizedSavings ?? o.summary?.realizedSavings,
                },
            };
        }
        if (isFlexibleOutput(o) && o.structuredOutputs) {
            return {
                ...o,
                structuredOutputs: o.structuredOutputs.map((so) => {
                    if (so.type !== StructuredOutputType.DEAL || !so.data.dealInfo) return so;
                    const { estimatedSavingsLow, estimatedSavingsHigh, ...dealInfo } =
                        so.data.dealInfo;
                    return { ...so, data: { ...so.data, dealInfo } };
                }),
            };
        }
        return o;
    };

    // Every save goes live, so every save is validated. `notify` is read by the
    // server only on a correction to an already delivered analysis.
    const handleSave = async (notify: boolean) => {
        if (isRfxOutput(output)) {
            if (!output.report?.content) {
                message.error("Cannot save: the report is blank");
                return;
            }
        } else if (isDealOutput(output)) {
            if (!output.dealInfo?.vendorKey) {
                message.error("Please select a vendor");
                return;
            }
            if (!output.report) {
                message.error("Cannot save: the report is blank");
                return;
            }
        } else if (isFlexibleOutput(output)) {
            for (const so of output.structuredOutputs ?? []) {
                if (so.type === StructuredOutputType.DEAL && !so.data.dealInfo?.vendorKey) {
                    message.error("Please select a vendor");
                    return;
                }
            }
            // A save that completes the analysis delivers a report; only an
            // already completed analysis takes edits to its structured data
            // without one.
            if (!output.content && analysis.status !== ProjectAnalysisStatus.COMPLETED) {
                message.error("Cannot save: content is blank");
                return;
            }
        }

        const delivers = saveDelivers(analysis, output);
        setSaving(true);
        try {
            await saveAnalysisOutput(project.id, analysis.version, {
                notify,
                output: withCurrentSavingsShape(output),
            });
            message.success(
                delivers
                    ? "Analysis completed and shared with user"
                    : notify
                      ? "Analysis saved and users notified"
                      : "Analysis saved"
            );
            await onSuccess();
            onClose();
        } catch (error) {
            handleError(error);
        } finally {
            setSaving(false);
        }
    };

    return { output, setOutput, loading, saving, handleSave, handleOutputTypeChange, resetState };
}

export const ModalSaveAnalysisOutput: FC<ModalSaveAnalysisOutputProps> = (props) => {
    const { open, state, onClose } = props;
    const { project, analysis } = state;
    const { output, setOutput, loading, saving, handleSave, handleOutputTypeChange, resetState } =
        useAnalysisOutputState(props);
    const defaultTab = isFlexibleOutput(output)
        ? "content"
        : isRfxOutput(output)
          ? "report"
          : "deal";
    const [activeTab, setActiveTab] = useState(defaultTab);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    // Whether a correction tells the project's users. A delivery always does.
    const [notify, setNotify] = useState(false);
    useEffect(() => {
        if (open) setNotify(false);
    }, [open]);
    const delivers = saveDelivers(analysis, output);

    const availableOutputTypeOptions = outputTypeOptions.filter(
        (option) =>
            option.value === AnalysisOutputType.FLEXIBLE ||
            option.value === analysis.outputType ||
            option.value === output.type
    );

    // Refresh attachments by calling onSuccess (re-fetches project)
    const refreshAttachments = async () => {
        await props.onSuccess();
    };

    const handleClose = () => {
        setShowCloseConfirm(true);
    };

    const handleConfirmDiscard = () => {
        setShowCloseConfirm(false);
        setActiveTab(defaultTab);
        resetState();
        onClose();
    };

    const handleConfirmSave = async () => {
        setShowCloseConfirm(false);
        await handleSave(delivers || notify);
    };

    const handleConfirmGoBack = () => {
        setShowCloseConfirm(false);
    };

    const getTabItems = () => {
        const attachmentsTab = {
            key: "attachments",
            label: `Attachments (${analysis.attachments?.length ?? 0})`,
            children: (
                <AttachmentsTab
                    projectId={project.id}
                    analysisVersion={analysis.version}
                    attachments={analysis.attachments || []}
                    onAttachmentsChange={refreshAttachments}
                />
            ),
        };

        if (isFlexibleOutput(output)) {
            return [
                {
                    key: "content",
                    label: "Content",
                    children: <FlexibleContentTab output={output} onChange={setOutput} />,
                },
                attachmentsTab,
                {
                    key: "structured",
                    label: `Structured (${output.structuredOutputs?.length ?? 0})`,
                    children: (
                        <FlexibleStructuredOutputsTab
                            output={output}
                            onChange={setOutput}
                            project={project}
                        />
                    ),
                },
            ];
        }

        if (isRfxOutput(output)) {
            return [
                {
                    key: "report",
                    label: "Report",
                    children: <RfxReportTab output={output} onChange={setOutput} />,
                },
                {
                    key: "vendors",
                    label: `Vendors (${output.vendors?.length ?? 0})`,
                    children: <RfxVendorsTab output={output} onChange={setOutput} />,
                },
                attachmentsTab,
            ];
        }

        // Deal output
        return [
            {
                key: "deal",
                label: "Deal info",
                children: (
                    <DealInfoTab
                        output={output}
                        onChange={setOutput}
                        currentNegotiationStatus={project.orderForm?.negotiationStatus}
                    />
                ),
            },
            {
                key: "summary",
                label: "Summary",
                children: <SummaryTab output={output} onChange={setOutput} />,
            },
            {
                key: "report",
                label: "Report",
                children: (
                    <DealReportTab
                        projectId={project.id}
                        analysisVersion={analysis.version}
                        output={output}
                        onChange={setOutput}
                    />
                ),
            },
            {
                key: "levers",
                label: `Levers (${output.levers?.length ?? 0})`,
                children: <LeversTab output={output} onChange={setOutput} />,
            },
            attachmentsTab,
        ];
    };

    // Reset active tab when output type changes
    useEffect(() => {
        if (isFlexibleOutput(output)) {
            setActiveTab("content");
        } else if (isRfxOutput(output)) {
            setActiveTab("report");
        } else {
            setActiveTab("deal");
        }
    }, [output.type]);

    return (
        <>
            <Modal
                title="Save analysis output"
                open={open}
                onCancel={handleClose}
                width="calc(100vw - 48px)"
                footer={[
                    <Button key="cancel" onClick={handleClose} disabled={saving}>
                        Cancel
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        onClick={() => handleSave(delivers || notify)}
                        loading={saving}
                        disabled={loading}
                    >
                        Save
                    </Button>,
                    <Tooltip
                        key="notify"
                        title={
                            delivers
                                ? "The first delivery always notifies the project's users and moves its Updated date."
                                : "Also moves the project's Updated date. Leave unchecked for corrections the users do not need to hear about."
                        }
                    >
                        {/* The span keeps the tooltip working while the checkbox is disabled. */}
                        <span style={{ marginLeft: 16 }}>
                            <Checkbox
                                checked={delivers || notify}
                                disabled={delivers || saving}
                                onChange={(e) => setNotify(e.target.checked)}
                            >
                                Notify users
                            </Checkbox>
                        </span>
                    </Tooltip>,
                ]}
            >
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <div>
                        <span style={{ marginRight: 8 }}>Output type:</span>
                        <Select
                            value={output.type}
                            onChange={handleOutputTypeChange}
                            options={availableOutputTypeOptions}
                            style={{ width: 160 }}
                            size="small"
                            disabled={loading}
                        />
                    </div>
                    {loading ? (
                        <div style={{ textAlign: "center", padding: "48px 0" }}>
                            <Spin />
                        </div>
                    ) : (
                        <Tabs activeKey={activeTab} items={getTabItems()} onChange={setActiveTab} />
                    )}
                </Space>
            </Modal>

            {/* Close Confirmation Modal */}
            <Modal
                title="Unsaved changes"
                open={showCloseConfirm}
                onCancel={handleConfirmGoBack}
                footer={[
                    <Button key="discard" danger onClick={handleConfirmDiscard}>
                        Discard
                    </Button>,
                    <Button key="goback" onClick={handleConfirmGoBack}>
                        Go Back
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        onClick={handleConfirmSave}
                        loading={saving}
                    >
                        Save
                    </Button>,
                ]}
            >
                Do you want to save your changes before closing?
            </Modal>
        </>
    );
};
